/* eslint-disable react/default-props-match-prop-types, react/require-default-props */
import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
// Styles
import styled, { css } from 'styled-components';
import { lighten, darken } from 'polished';
import {
  Theme as theme,
} from '@uaveiro/ui'
import { PORTAL_WWW } from 'services/constants'

const sizing = {
  xSmall: `
    font-size: ${theme.sizes.xs}; //12px
  `,
  small: `
    font-size: ${theme.sizes.s}; //14px
  `,
  article: `
    font-size: ${theme.sizes.sm};; //15px
  `,
  mediumSmall: `
    font-size: ${theme.sizes.m}; //16px
  `,
  medium: `
    font-size: ${theme.sizes.l}; //18px
  `,
  large: `
    font-size: ${theme.sizes.xl2}; //22px
  `,
  xLarge: `
    font-size: ${theme.sizes.xl3m}; //28px
  `,
  xxLarge: `
    font-size: ${theme.sizes.xl4}; //32px
  `,
  xxxLarge: `
    font-size: ${theme.sizes.xl6}; //48px
  `
};

const sharedStyle = css`
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
  color: ${props => props.color || props.theme.components.link.color};
  ${props => (props.fontSize
    ? `font-size: ${props.fontSize}`
    : sizing[props.size || 'small'])};
  line-height: ${props => props.lineHeight};
  font-weight: ${props => props.fontWeight || '400'};
  text-decoration: ${props => props.textDecoration || 'none'};
  background: ${props => props.background};
  transform: ${props => props.transform};
  text-transform: ${props => props.textTransform};
  margin: ${props => props.margin};
  padding: ${props => props.padding};
  padding-bottom: ${props => props.paddingBottom};
  cursor: ${props => props.cursor || 'pointer'};
  position: ${props => props.position};
  right: ${props => props.right};
  padding-right: ${props => props.paddingRight};
  border-right: ${props => props.borderRight};
  margin-bottom: ${props => props.marginBottom};
  margin-top: ${props => props.marginTop};
  display: ${props => props.display};
  height: ${props => props.height};
  width: ${props => props.width};
  text-decoration-color: ${props => props.textDecorationColor};
  -webkit-text-decoration-color: ${props => props.textDecorationColor};
  ${props => props.uppercase && 'text-transform: uppercase'};
  ${props => props.opacity && 'opacity: 0.8'};
  &:focus {
    outline: 2px solid inherit;
    outline-offset: ${props => props.outlineOffset};
  }
  //Alt styles
  img {
    color: #000;
    font-weight: 300;
  }
  ${props => (props.hasHover
    ? `&:hover {
    text-decoration: ${props.fontDecoration};
    color: ${
    props.default
      ? props.textDecorationColor
      : lighten(0.1, props.color || props.theme.components.link.color)
    };
    ${props.lighten && 'opacity: 0.5;'};
    text-decoration-color: ${props.textDecorationColor};
    -webkit-text-decoration-color: ${props.textDecorationColor};
    /*
    -- Icon Styles when hover a link
    */
    ${props.hasIconHover
      && `
      i,
      p {
        ${
    props.darken
      ? `color: ${darken(
        0.5,
        props.color || props.theme.components.link.color
      )}`
      : `color: ${lighten(
        0.5,
        props.color || props.theme.components.link.color
      )}`
    };
      }
    `};
  }`
    : '')};
  &:hover {
    color: ${props => props.hoverColor};
  }
  /*
  -- Responsive
  */
  // Medium devices (tablets, less than 992px)
  @media (max-width: 992px) {
    margin-bottom: ${props => props.mdMarginBottom};
  }
  // Small devices (landscape phones, less than 768px)
  @media (max-width: 767px) {
    font-size: ${props => props.mobileSmFontSize};
    padding: ${props => props.mobileSmPadding};
  }
  // Extra small devices (portrait phones, less than 576px)
  @media (max-width: 575px) {
    font-size: ${props => props.mobileXsFontSize};
    line-height: ${props => props.mobileXsLineHeight};
  }
`;

const NativeLink = styled.a.attrs(({ text, children }) => ({
  children: text || children
}))`
  ${sharedStyle}
`;

const Link = memo(({
  href, ...rest
}) => {
  const propsComputed = useMemo(() => {
    const isExternal = href.indexOf('http') >= 0
    if (isExternal) return { href, target: '_blank' }
    return {
      href: `${PORTAL_WWW}${href}`,
      target: '_blank'
    }
  }, [href])
  return (
    <NativeLink {...rest} {...propsComputed} />
  );
})

Link.defaultProps = {
  fontDecoration: 'underline',
  size: 'small',
  uppercase: false,
  lighten: false,
  opacity: false,
  disabled: false,
  native: false,
  hasHover: true,
  hasIconHover: true
};

Link.propTypes = {
  href: PropTypes.string,
  native: PropTypes.bool,
  disabled: PropTypes.bool,
  color: PropTypes.string,
  fontSize: PropTypes.string,
  size: PropTypes.string,
  lineHeight: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  textDecoration: PropTypes.string,
  background: PropTypes.string,
  transform: PropTypes.string,
  textTransform: PropTypes.string,
  margin: PropTypes.string,
  padding: PropTypes.string,
  // paddingBottom: PropTypes.string,
  cursor: PropTypes.string,
  position: PropTypes.string,
  right: PropTypes.string,
  // paddingRight: PropTypes.string,
  // borderRight: PropTypes.string,
  marginBottom: PropTypes.string,
  marginTop: PropTypes.string,
  display: PropTypes.string,
  height: PropTypes.string,
  width: PropTypes.string,
  textDecorationColor: PropTypes.string,
  uppercase: PropTypes.bool,
  opacity: PropTypes.bool,
  outlineOffset: PropTypes.string,
  // hasHover: PropTypes.bool,
  // fontDecoration: PropTypes.string,
  default: PropTypes.bool,
  hasIconHover: PropTypes.bool,
  // darken: PropTypes.bool,
  lighten: PropTypes.bool,
  hoverColor: PropTypes.string,
  mdMarginBottom: PropTypes.string,
  mobileSmFontSize: PropTypes.string,
  mobileSmPadding: PropTypes.string,
  mobileXsFontSize: PropTypes.string,
  mobileXsLineHeight: PropTypes.string
};

export default Link;
